import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, LabelList, ResponsiveContainer } from 'recharts';

const COLORS = {
  correct: 'var(--q3-green-normal)',
  incorrect: 'var(--q3-danger-normal)',
  unattempted: 'var(--q3-neutral-light)',
};

export default function QualityOfTimeChart({ qualityOfTime, selectedSubject }) {
  const [chartData, setChartData] = useState([]);
  const [totalTime, setTotalTime] = useState(0);

  useEffect(() => {
    if (!qualityOfTime || !selectedSubject?.title) return;

    const subjectKey = selectedSubject.title.toLowerCase();
    const data = qualityOfTime[subjectKey];

    if (data) {
      const correct = data.timeSpentCorrect || 0;
      const incorrect = data.timeSpentIncorrect || 0;
      const unattempted = data.timeSpentUnattempted || 0;
      const total = correct + incorrect + unattempted;

      if (total === 0) {
        setChartData([]);
        setTotalTime(0);
        return;
      }
      setTotalTime(total);
      setChartData([
        {
          name: 'Time Spent',
          timeSpentCorrect: +((correct / total) * 100).toFixed(1),
          timeSpentIncorrect: +((incorrect / total) * 100).toFixed(1),
          timeSpentUnattempted: +((unattempted / total) * 100).toFixed(1),
          correctMin: correct,
          incorrectMin: incorrect,
          unattemptedMin: unattempted,
        },
      ]);
    }
  }, [qualityOfTime, selectedSubject]);

  return (
    <div className="w-full mb-8 flex flex-col gap-2 border border-[var(--q3-stroke-light)] border-t-0 pb-4 rounded-xl">
      <div className="flex flex-row overflow-x-auto whitespace-nowrap min-w-0 bg-[var(--q3-surface-dim)] gap-6 py-4 px-4">
        <Legend color={COLORS.correct} label="Time on Correct Qs" value={chartData[0]?.correctMin} />
        <Legend color={COLORS.incorrect} label="Time on Incorrect Qs" value={chartData[0]?.incorrectMin} />
        <Legend color={COLORS.unattempted} label="Time on Unattempted Qs" value={chartData[0]?.unattemptedMin} />
      </div>

      <div className="w-full px-2 pointer-events-none" style={{ height: 50 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart layout="vertical" data={chartData} barCategoryGap={70}>
            <XAxis type="number" domain={[0, 100]} hide />
            <YAxis type="category" dataKey="name" hide />
            <Bar dataKey="timeSpentCorrect" stackId="a" fill={COLORS.correct}>
              <LabelList
                position="insideLeft"
                formatter={() => `${chartData[0]?.timeSpentCorrect}%`}
                style={{ fontSize: 14, fontWeight: 600, fill: 'var(--q3-surface-default)' }}
              />
            </Bar>
            <Bar dataKey="timeSpentIncorrect" stackId="a" fill={COLORS.incorrect}>
              <LabelList
                position="insideLeft"
                formatter={() => `${chartData[0]?.timeSpentIncorrect}%`}
                style={{ fontSize: 14, fontWeight: 600, fill: 'var(--q3-surface-default)' }}
              />
            </Bar>
            <Bar dataKey="timeSpentUnattempted" stackId="a" fill={COLORS.unattempted}>
              <LabelList
                position="insideLeft"
                formatter={() => `${chartData[0]?.timeSpentUnattempted}%`}
                style={{ fontSize: 14, fontWeight: 600, fill: 'var(--q3-surface-default)' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="text-[var(--q3-neutral-default)] px-4">
        Overall Time: <span className="font-bold">{totalTime} mins</span>
      </div>
    </div>
  );
}

function Legend({ color, label, value }) {
  return (
    <div className="flex flex-row items-center gap-2 shrink-0 min-w-[180px]">
      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color }} />
      <p className="text-[var(--q3-neutral-default)] text-sm font-medium whitespace-nowrap">
        {label}: <span className="font-normal">{value ?? 0} mins</span>
      </p>
    </div>
  );
}
